/**
 * Widget registry: dash:viewer → widget.
 *
 * The shape's dash:viewer wins when it is a viewer we implement. Otherwise
 * the widget follows from the values, the way DASH scores its default
 * viewers: WKT gets a map, language strings the language viewer, many
 * related objects the value table, other IRIs links, the rest literals.
 * A widget's render returns null when it has nothing to show, which hides the row.
 */

import { DASH, DVIEW, settingList, type ViewPropertyShape } from '@utils/viewShapes.utils';
import type { EntityObject } from '@/types';
import {
  DetailsViewer,
  GeometryViewer,
  LabelViewer,
  LangStringViewer,
  LiteralViewer,
  URIViewer,
  ValueTableViewer,
} from './viewers';
import { AlongTrackViewer } from './alongTrack';
import { FacilitiesViewer, KeyFigureViewer, KmViewer, ScaleBarViewer, ScaleViewer, SymbolViewer, YesNoViewer } from './figures';
import { LinePositionViewer } from './line';
import { SpotViewer } from './spot';
import { AreaLocationViewer, ContentsViewer, contentsText, hasArea, hasContents, hasEndedTracks, TrackPeriodsViewer } from './area';
import { TopologyViewer } from './topology';
import { BranchViewer } from './branches';
import { TrackGraphViewer } from './trackGraph';
import { ValidityViewer } from './validity';
import type { Widget, WidgetEntry, WidgetProps } from './types';

const RDF_LANG_STRING = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#langString';
const WKT_LITERAL = 'http://www.opengis.net/ont/geosparql#wktLiteral';

/** Above this many IRI values a plain link list becomes a value table. */
const VALUE_TABLE_THRESHOLD = 5;

export const GEOMETRY_VIEWER = 'urn:dim:viewer:geometry';

/** A viewer of the row's own values: nothing to show without values. */
const own = (Component: Widget, options: Omit<WidgetEntry, 'render'> = {}): WidgetEntry => ({
  ...options,
  render: (props) => (props.values.length > 0 ? <Component {...props} /> : null),
});

/** A viewer that reads other rows by key: `has` says whether those rows give it something to draw. */
const reading = (
  Component: Widget,
  has: (props: WidgetProps) => boolean,
  options: Omit<WidgetEntry, 'render'> = {}
): WidgetEntry => ({
  ...options,
  render: (props) => (has(props) ? <Component {...props} /> : null),
});

const keyed = (props: WidgetProps, name: string): boolean =>
  settingList(props.property, name).some((key) => {
    const row = props.context.model?.rows[key];
    return Boolean(row && ((props.context.model?.values[row.id]?.length ?? 0) > 0 || (props.context.model?.tables[row.id]?.items.length ?? 0) > 0));
  });

const WIDGETS: Record<string, WidgetEntry> = {
  [DASH.LabelViewer]: own(LabelViewer),
  [DASH.LiteralViewer]: own(LiteralViewer),
  [DASH.LangStringViewer]: own(LangStringViewer),
  [DASH.URIViewer]: own(URIViewer),
  [DASH.DetailsViewer]: own(DetailsViewer),
  [DASH.ValueTableViewer]: own(ValueTableViewer, { wide: true }),
  [GEOMETRY_VIEWER]: own(GeometryViewer, { wide: true }),
  [DVIEW.LinearLocationViewer]: reading(AlongTrackViewer, (props) => Boolean(props.context.stretch), { wide: true, hero: 'along' }),
  [DVIEW.AreaLocationViewer]: reading(AreaLocationViewer, (props) => hasArea(props.context, props.property), { wide: true, hero: 'along' }),
  [DVIEW.BranchViewer]: reading(BranchViewer, (props) => (props.context.model?.tables[props.property.id]?.items.length ?? 0) > 0, { hero: 'along' }),
  [DVIEW.TopologyViewer]: reading(TopologyViewer, (props) => (props.context.model?.tables[props.property.id]?.items.length ?? 0) > 0, { wide: true, hero: 'graph' }),
  // Without neighbours the graph still says what the track is part of and what sits on it.
  [DVIEW.TrackGraphViewer]: reading(TrackGraphViewer, (props) => Boolean(props.context.model), { wide: true, hero: 'graph' }),
  [DVIEW.KeyFigureViewer]: own(KeyFigureViewer),
  [DVIEW.KmViewer]: own(KmViewer),
  [DVIEW.ScaleBarViewer]: reading(ScaleBarViewer, (props) => props.values.length > 0 || keyed(props, 'value'), { picture: true }),
  [DVIEW.SymbolViewer]: reading(SymbolViewer, (props) => props.values.length > 0 || keyed(props, 'value'), { picture: true }),
  [DVIEW.LinePositionViewer]: reading(LinePositionViewer, (props) => props.values.length > 0 || keyed(props, 'value'), { picture: true }),
  [DVIEW.ValidityViewer]: own(ValidityViewer, { picture: true }),
  [DVIEW.ScaleViewer]: own(ScaleViewer, { picture: true }),
  [DVIEW.YesNoViewer]: own(YesNoViewer),
  [DVIEW.TrackPeriodsViewer]: reading(TrackPeriodsViewer, (props) => hasEndedTracks(props.context, props.property)),
  [DVIEW.ContentsViewer]: reading(ContentsViewer, (props) => hasContents(props.context, props.property), {
    wide: true,
    picture: true,
    text: (props) => contentsText(props.context, props.property),
  }),
  // Only for objects at a km of their own (dview:at)
  [DVIEW.SpotLocationViewer]: reading(
    SpotViewer,
    (props) =>
      (keyed(props, 'at') || (keyed(props, 'kmFrom') && keyed(props, 'kmTo'))) &&
      (props.context.model?.tables[props.property.id]?.items.length ?? 0) > 0,
    { wide: true, hero: 'along' }
  ),
  [DVIEW.FacilitiesViewer]: reading(FacilitiesViewer, (props) => settingList(props.property, 'facility').length > 0, { wide: true, picture: true }),
};
WIDGETS[DVIEW.AlongTrackViewer] = WIDGETS[DVIEW.LinearLocationViewer];
WIDGETS[DVIEW.SpotViewer] = WIDGETS[DVIEW.SpotLocationViewer];

/** Viewer IRI for a property shape and its values. */
export function chooseViewer(property: ViewPropertyShape, values: EntityObject[]): string {
  if (
    property.datatypes.includes(WKT_LITERAL) ||
    values.some((value) => value.datatype === WKT_LITERAL)
  ) {
    return GEOMETRY_VIEWER;
  }
  if (property.viewer && WIDGETS[property.viewer]) return property.viewer;

  const iriValues = values.filter((value) => value.type === 'uri').length;
  if (iriValues > VALUE_TABLE_THRESHOLD) return DASH.ValueTableViewer;
  if (iriValues > 0) return DASH.URIViewer;
  if (property.datatypes.includes(RDF_LANG_STRING) || values.some((value) => value.lang)) {
    return DASH.LangStringViewer;
  }
  return DASH.LiteralViewer;
}

export function widgetFor(viewer: string): WidgetEntry {
  return WIDGETS[viewer] ?? WIDGETS[DASH.LiteralViewer];
}
